import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../services/api';
import { ActionButtons, PaymentActionModal, can } from '../components/PaymentActions';
import { toast } from 'react-toastify';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowPathIcon,
  TruckIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const Orders = () => {
  const { orders, loading, refreshData } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const { user } = useAuth();
  const role = user?.role || 'viewer';

  const loadDetail = (ord) => {
    if (!ord) { setOrderDetail(null); return; }
    setLoadingDetail(true);
    adminApi.getOrderDetails(ord.public_order_id || ord.id)
      .then((res) => setOrderDetail(res.data))
      .catch(() => setOrderDetail(null))
      .finally(() => setLoadingDetail(false));
  };

  // Fetch live Stripe/Shippo truth whenever a different order is opened.
  useEffect(() => { loadDetail(selectedOrder); /* eslint-disable-next-line */ }, [selectedOrder]);

  const handleExport = async () => {
    try {
      const res = await adminApi.exportOrders();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'orders.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  const statuses = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'succeeded', label: 'Completed' },
    { value: 'COMPLETED', label: 'PayPal Completed' },
    { value: 'requires_action', label: 'Requires Action' },
    { value: 'canceled', label: 'Cancelled' },
    { value: 'failed', label: 'Failed' },
  ];

  const filteredOrders = orders?.filter(order => {
    const matchesSearch = 
      order.public_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.payment_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'requires_action':
        return 'bg-blue-100 text-blue-800';
      case 'canceled':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const OrderModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-4 sm:top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 max-h-[90vh] overflow-y-auto shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Order Details - #{order.public_order_id || order.id?.slice(-8)}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order Status</label>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.payment_status)}`}
                  >
                    {order.payment_status || 'pending'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer</label>
                  <p className="mt-1 text-sm text-gray-900">{order.customer_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Provider</label>
                  <p className="mt-1 text-sm text-gray-900">{order.payment_provider || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{order.payment_id || order.stripe_payment_id || 'N/A'}</p>
                </div>
              </div>

              {/* Live payment (Stripe) truth + actions */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2" /> Live Payment (Stripe)
                </h4>
                {loadingDetail && <p className="text-sm text-gray-400">Loading live status…</p>}
                {!loadingDetail && orderDetail?.stripe_payment && !orderDetail.stripe_payment.error && (() => {
                  const sp = orderDetail.stripe_payment;
                  const item = {
                    orderId: orderDetail.order?.public_order_id || order.public_order_id,
                    paymentId: sp.id, stripeStatus: sp.status,
                    amount: sp.amount, amountReceived: sp.amount_received, amountCapturable: sp.amount_capturable,
                    email: orderDetail.order?.user_email,
                  };
                  return (
                    <div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-400 text-xs">Stripe status</span><br /><span className="font-medium">{sp.status}</span></div>
                        <div><span className="text-gray-400 text-xs">Authorized</span><br />${((sp.amount || 0) / 100).toFixed(2)}</div>
                        <div><span className="text-gray-400 text-xs">Received</span><br />${((sp.amount_received || 0) / 100).toFixed(2)}</div>
                        <div><span className="text-gray-400 text-xs">Card</span><br />{sp.card ? `${sp.card.brand} ••${sp.card.last4}` : '—'}</div>
                      </div>
                      {sp.refunds?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">Refunds: {sp.refunds.map((r) => `$${((r.amount || 0) / 100).toFixed(2)} (${r.status})`).join(', ')}</p>
                      )}
                      <div className="mt-3"><ActionButtons item={item} role={role} onAct={(action, it) => setActionModal({ action, item: it })} /></div>
                    </div>
                  );
                })()}
                {!loadingDetail && orderDetail?.stripe_payment?.error && (
                  <p className="text-sm text-red-500">Stripe: {orderDetail.stripe_payment.error}</p>
                )}
                {!loadingDetail && orderDetail && !orderDetail.stripe_payment && (
                  <p className="text-sm text-gray-400">No Stripe PaymentIntent for this order.</p>
                )}
                {orderDetail?.shippo_transaction && !orderDetail.shippo_transaction.error && (
                  <p className="text-xs text-gray-500 mt-3">
                    Shippo: <span className="font-medium">{orderDetail.shippo_transaction.status}</span>
                    {orderDetail.shippo_transaction.tracking_status ? ` · tracking ${orderDetail.shippo_transaction.tracking_status}` : ''}
                    {orderDetail.shippo_transaction.label_url ? <> · <a className="text-indigo-600 underline" href={orderDetail.shippo_transaction.label_url} target="_blank" rel="noreferrer">label</a></> : ''}
                  </p>
                )}
              </div>

              {/* Shipping Info */}
              {(order.tracking_number || order.carrier) && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                    <TruckIcon className="h-5 w-5 mr-2" />
                    Shipping Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.tracking_number && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tracking Number</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{order.tracking_number}</p>
                      </div>
                    )}
                    {order.carrier && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Carrier</label>
                        <p className="mt-1 text-sm text-gray-900">{order.carrier}</p>
                      </div>
                    )}
                    {order.service_name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Service</label>
                        <p className="mt-1 text-sm text-gray-900">{order.service_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Addresses */}
              {(order.shipping_address || order.billing_address) && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Addresses</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.shipping_address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping Address</label>
                        <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                          <p>{order.shipping_address.name}</p>
                          <p>{order.shipping_address.street}</p>
                          <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                          {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
                        </div>
                      </div>
                    )}
                    {order.billing_address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Address</label>
                        <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                          <p>{order.billing_address.name}</p>
                          <p>{order.billing_address.street}</p>
                          <p>{order.billing_address.city}, {order.billing_address.state} {order.billing_address.zip}</p>
                          {order.billing_address.country && <p>{order.billing_address.country}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Info */}
              {(order.payment_method || order.stripe_payment_id) && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Payment Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.payment_method && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                        <p className="mt-1 text-sm text-gray-900">{order.payment_method}</p>
                      </div>
                    )}
                    {order.stripe_payment_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Stripe Payment ID</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{order.stripe_payment_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              {order.items && order.items.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Order Items</h4>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-md flex justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">Quantity: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-medium text-gray-900">${item.price}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !orders) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <div className="flex gap-2">
          {can(role, 'finance') && (
            <button
              onClick={handleExport}
              className="inline-flex items-center min-h-[44px] px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Export CSV
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center min-h-[44px] px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Orders Table (desktop) */}
      <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{order.public_order_id || order.id?.slice(-8) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.customer_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.payment_status)}`}
                      >
                        {order.payment_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                      ${order.amount_paid || order.amount || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="inline-flex items-center min-h-[44px] px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'No orders found matching your criteria.' 
                      : 'No orders found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Orders Cards (mobile) */}
      <div className="md:hidden space-y-3">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <div key={order.id} className="bg-white shadow rounded-md p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  #{order.public_order_id || order.id?.slice(-8) || 'N/A'}
                </span>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.payment_status)}`}
                >
                  {order.payment_status || 'pending'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                <div>
                  <span className="block text-xs text-gray-400">Customer</span>
                  <span className="text-gray-900">{order.customer_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-xs text-gray-400">Amount</span>
                  <span className="text-gray-900 font-semibold">${order.amount_paid || order.amount || '0.00'}</span>
                </div>
                <div className="col-span-2">
                  <span className="block text-xs text-gray-400">Date</span>
                  <span className="text-gray-900">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="inline-flex items-center justify-center w-full min-h-[44px] px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white shadow rounded-md px-6 py-8 text-center text-gray-500">
            {searchTerm || statusFilter !== 'all'
              ? 'No orders found matching your criteria.'
              : 'No orders found.'}
          </div>
        )}
      </div>

      {/* Order Modal */}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {/* Payment action confirm modal */}
      {actionModal && (
        <PaymentActionModal
          action={actionModal.action}
          item={actionModal.item}
          onClose={() => setActionModal(null)}
          onDone={() => {
            setActionModal(null);
            loadDetail(selectedOrder); // refresh live Stripe state in the open modal
            handleRefresh();           // refresh the orders list
          }}
        />
      )}
    </div>
  );
};

export default Orders;
